// SHGManagement.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chart, registerables } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleDown,
  faArrowCircleUp,
  faComments,
} from "@fortawesome/free-solid-svg-icons";

Chart.register(...registerables);

const SHGManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("funds");
  const [expandedRows, setExpandedRows] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRank, setFilterRank] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Dummy SHG + Member Data
  const shgs = [
    {
      id: 1,
      name: "Saksham Gat",
      district: "Pune",
      members: 12,
      fundReceived: 50000,
      fundUsed: 30000,
      digitalTransactions: 25,
      advisorNotes: "Good progress on digital adoption",
      maturityRank: "Gold",
      membersData: [
        { id: 1, name: "Sita", loan: 5000, repayment: 2000, trainings: 2, digitalTx: 5, advisorNote: "Very active" },
        { id: 2, name: "Gita", loan: 3000, repayment: 3000, trainings: 3, digitalTx: 4, advisorNote: "Needs improvement" },
      ],
    },
    {
      id: 2,
      name: "Gaurav Gat",
      district: "Nagpur",
      members: 15,
      fundReceived: 70000,
      fundUsed: 40000,
      digitalTransactions: 40,
      advisorNotes: "Needs more follow-up",
      maturityRank: "Silver",
      membersData: [
        { id: 1, name: "Radha", loan: 7000, repayment: 5000, trainings: 1, digitalTx: 3, advisorNote: "Average" },
        { id: 2, name: "Sanjana", loan: 6000, repayment: 3000, trainings: 2, digitalTx: 5, advisorNote: "Good" },
      ],
    },
  ];

  const handleSort = (column) => {
  const newOrder = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
  setSortColumn(column);
  setSortOrder(newOrder);
};

// Derived data
const filteredSHGs = shgs
  .filter((s) =>
    (s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.district.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.advisorNotes.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (filterRank ? s.maturityRank === filterRank : true)
  )
  .sort((a, b) => {
    if (!sortColumn) return 0;
    const map = {
      "Fund Received": "fundReceived",
      "Fund Used": "fundUsed",
      "Digital Tx": "digitalTransactions",
    };
    const key = map[sortColumn];
    return sortOrder === "asc" ? a[key] - b[key] : b[key] - a[key];
  });

  const toggleRow = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Chart Data
  const fundChartData = {
    labels: shgs.map((s) => s.name),
    datasets: [
      { label: "Fund Received", data: shgs.map((s) => s.fundReceived), backgroundColor: "#FF6384" },
      { label: "Fund Used", data: shgs.map((s) => s.fundUsed), backgroundColor: "#36A2EB" },
    ],
  };

  const maturityChartData = {
    labels: shgs.map((s) => s.name),
    datasets: [
      {
        label: "Maturity Rank (1=Gold,2=Silver,3=Bronze)",
        data: shgs.map((s) =>
          s.maturityRank === "Gold" ? 1 : s.maturityRank === "Silver" ? 2 : 3
        ),
        backgroundColor: "#FFCE56",
      },
    ],
  };

  const digitalChartData = {
    labels: shgs.map((s) => s.name),
    datasets: [
      {
        label: "Digital Transactions",
        data: shgs.map((s) => s.digitalTransactions),
        borderColor: "#4BC0C0",
        backgroundColor: "rgba(75,192,192,0.2)",
        fill: true,
      },
    ],
  };

  return (
    <div className="bg-gray-900 min-h-screen text-white p-4">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-pink-500">SHG & Bachat Gat Management</h1>
        <p className="text-gray-400">Think5Whys Advisor Network</p>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-3 mb-4 place-items-center">
        {[
          { key: "funds", label: "Funds & Loan Workflow" },
          { key: "maturity", label: "SHG Maturity Ranking" },
          { key: "advisor", label: "Advisor Tracking" },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`px-4 py-2 rounded mr-2 mb-2 ${activeTab === tab.key ? "bg-pink-500" : "bg-gray-700"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="w-full">
        {activeTab === "funds" && (
            <div className="flex flex-col justify-center items-center w-full">
                {/* Header */}
                <h2 className="text-xl font-semibold mb-4 text-center">Fund & Loan Status</h2>

                {/* Chart */}
                <div className="w-full max-w-4xl mb-6">
                <div className="bg-gray-800 p-4 rounded shadow">
                    <Bar data={fundChartData} />
                </div>
                </div>

                {/* Search + Filter */}
                <div className="w-full flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search SHG or District..."
                    className="w-full md:w-1/2 p-2 rounded bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring focus:ring-pink-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Filter */}
                <select
                    className="w-full md:w-1/4 p-2 rounded bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring focus:ring-blue-500"
                    value={filterRank}
                    onChange={(e) => setFilterRank(e.target.value)}
                >
                    <option value="">All Ranks</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Bronze">Bronze</option>
                </select>
                </div>

                {/* Table */}
                <div className="overflow-x-auto w-full">
                <table className="min-w-full bg-gray-800 rounded border border-gray-700">
                    <thead>
                    <tr className="bg-gray-700 text-gray-200">
                        {[
                        "SHG Name",
                        "District",
                        "Members",
                        "Fund Received",
                        "Fund Used",
                        "Remaining",
                        "Digital Tx",
                        "Advisor Notes",
                        "Maturity Rank",
                        "Actions",
                        ].map((th, idx) => {
                        const sortable = ["Fund Received", "Fund Used", "Digital Tx"].includes(th);
                        return (
                            <th
                            key={idx}
                            className={`px-3 py-2 border border-gray-600 text-left text-sm md:text-base ${
                                sortable ? "cursor-pointer hover:bg-gray-600" : ""
                            }`}
                            onClick={() => sortable && handleSort(th)}
                            >
                            {th}
                            {sortable && sortColumn === th && (
                                <span className="ml-1 text-pink-400">
                                {sortOrder === "asc" ? "▲" : "▼"}
                                </span>
                            )}
                            </th>
                        );
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {filteredSHGs.map((s) => (
                        <React.Fragment key={s.id}>
                        {/* SHG Row */}
                        <tr className="hover:bg-gray-600 transition duration-200">
                            <td className="px-3 py-2">{s.name}</td>
                            <td className="px-3 py-2">{s.district}</td>
                            <td className="px-3 py-2">{s.members}</td>
                            <td className="px-3 py-2">₹{s.fundReceived}</td>
                            <td className="px-3 py-2">₹{s.fundUsed}</td>
                            <td className="px-3 py-2">₹{s.fundReceived - s.fundUsed}</td>
                            <td className="px-3 py-2">{s.digitalTransactions}</td>
                            <td className="px-3 py-2">{s.advisorNotes}</td>
                            <td className="px-3 py-2">{s.maturityRank}</td>
                            <td className="px-3 py-2 flex flex-wrap gap-2">
                            <button
                                className="bg-blue-600 px-2 py-1 rounded text-sm md:text-base"
                                onClick={() => toggleRow(s.id)}
                            >
                                {expandedRows.includes(s.id) ? "Hide Members" : "Show Members"}
                            </button>
                            <button className="bg-green-600 px-2 py-1 rounded text-sm md:text-base">
                                <FontAwesomeIcon icon={faArrowCircleDown} /> Loan
                            </button>
                            <button className="bg-yellow-600 px-2 py-1 rounded text-sm md:text-base">
                                <FontAwesomeIcon icon={faArrowCircleUp} /> Repay
                            </button>
                            <button className="bg-purple-600 px-2 py-1 rounded text-sm md:text-base">
                                <FontAwesomeIcon icon={faComments} /> Note
                            </button>
                            <button
                                className="bg-pink-500 px-2 py-1 rounded text-sm md:text-base"
                                onClick={() => navigate("/mbgscore")}
                            >
                                Ranking
                            </button>
                            </td>
                        </tr>

                        {/* Member Rows */}
                        {expandedRows.includes(s.id) &&
                            s.membersData.map((m) => (
                            <tr key={m.id} className="bg-gray-700 text-sm md:text-base">
                                <td className="px-3 py-2">👤 {m.name}</td>
                                <td className="px-3 py-2">Loan: ₹{m.loan}</td>
                                <td className="px-3 py-2">Repay: ₹{m.repayment}</td>
                                <td className="px-3 py-2">Trainings: {m.trainings}</td>
                                <td className="px-3 py-2">Digital Tx: {m.digitalTx}</td>
                                <td className="px-3 py-2" colSpan={3}>{m.advisorNote}</td>
                                <td className="px-3 py-2 flex justify-start">
                                <button
                                    className="bg-pink-500 px-2 py-1 rounded text-sm md:text-base"
                                    onClick={() => navigate("/sakshamshaktiscore")}
                                >
                                    Ranking
                                </button>
                                </td>
                            </tr>
                            ))}
                        </React.Fragment>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>
            )}



        {activeTab === "maturity" && (
          <div className="flex flex-col justify-center items-center mb-4">
            <h2 className="text-xl font-semibold mb-2">SHG Maturity Ranking</h2>
            <div className="w-full md:w-1/3 bg-gray-800 p-2 rounded">
              <Bar data={maturityChartData} />
            </div>
          </div>
        )}

        {activeTab === "advisor" && (
          <div className="flex flex-col justify-center items-center mb-4">
            <h2 className="text-xl font-semibold mb-2">Advisor Tracking</h2>
            <div className="w-full md:w-1/3 bg-gray-800 p-2 rounded">
              <Line data={digitalChartData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SHGManagement;
